import axios from 'axios';
import type { ScaleCell, ScaleConfig } from 'Types/ExerciseContentTypes';
import type { ScaleProgressCell } from 'Types/ExerciseProgressTypes';

/**
 * Client for the exercise-progress HTTP surface. That surface is owned by MUS-458
 * (the 4a/4b backend shipped the store + service but no controller); it is NOT live
 * yet, so the only caller - the 7c saga - stays dark behind USE_EXERCISES until it
 * lands. These signatures are the contract MUS-458 implements against.
 *
 * Follows the house saga/API idiom (see `Sagas/Repertoire.ts`): axios against
 * `${REACT_APP_BACKEND_URL}/api/v1/`, no `user_id` in the body (the backend derives
 * it from the authenticated request).
 */

const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/`;

/**
 * The earned-clear write body. Mirrors the backend `RecordClearDto` MINUS `user_id`
 * (auth-derived) and MINUS `cleared_at`: the "when cleared" timestamp is server-stamped
 * at transaction time. A server-authoritative store must not trust a client wall-clock,
 * so MUS-458 stamps it, period. `play_session_id` is optional and stays null until the
 * play-session lifecycle endpoint lands (also MUS-458).
 */
export interface ExerciseClearRequest {
  exercise_id: number;
  scale: ScaleCell['scale'];
  tonic: ScaleCell['tonic'];
  hand: ScaleConfig['hand'];
  range_octaves: number;
  direction: ScaleConfig['direction'];
  rhythm_density: ScaleConfig['rhythm_density'];
  tempo: number;
  mastered: boolean;
  play_session_id?: string | null;
  // MUS-459 - the app-session analytics id, threaded onto the clear so the backend's
  // point-earned + tier-completed emits carry a NOT-NULL analytics_session_id.
  analytics_session_id?: string | null;
}

/**
 * POST an earned clear. Returns the cell's canonical frontier AFTER the backend's
 * domination/prune, so the slice takes what this returns rather than guessing
 * (server-authoritative).
 */
export async function postExerciseClear(body: ExerciseClearRequest): Promise<ScaleProgressCell> {
  const res = await axios.post(`${baseUrl}exercise-progress/clears`, body);
  return res.data as ScaleProgressCell;
}

/** GET the per-cell frontier for the authenticated user and one exercise. */
export async function getExerciseProgress(exerciseId: number): Promise<ScaleProgressCell[]> {
  const res = await axios.get(`${baseUrl}exercise-progress`, {
    params: { exercise_id: exerciseId },
  });
  return res.data as ScaleProgressCell[];
}

/**
 * The open-a-sitting body for POST /exercise-play-session (MUS-459). `user_id` is
 * auth-derived server-side, never sent. The backend mints the row and emits the
 * session-started event; it returns the minted play_session_id.
 */
export interface OpenPlaySessionRequest {
  exercise_id: number;
  analytics_session_id: string;
  datetime: string;
  platform?: string;
  entry_context?: Record<string, unknown> | null;
}

/**
 * Open the sitting. Retries are disabled on this POST ('axios-retry' retries: 0): a
 * lost-response retry would mint a SECOND sitting, so the saga owns the one retry and
 * keys it on not-yet-holding an id (call open once per sitting).
 */
export async function openPlaySession(body: OpenPlaySessionRequest): Promise<string> {
  const NO_RETRY = {
    'axios-retry': { retries: 0, retryCondition: () => false } as {
      retries: number;
      retryCondition: () => boolean;
    },
  };
  const res = await axios.post(`${baseUrl}exercise-play-session`, body, NO_RETRY);
  return (res.data as { play_session_id: string }).play_session_id;
}

/** The close-a-sitting body for POST /exercise-play-session/:id/close (MUS-459). */
export interface ClosePlaySessionRequest {
  analytics_session_id: string;
  datetime: string;
  platform?: string;
}

/** Close the sitting: the backend persists the telemetry aggregate and emits the
 *  tier-session-finished event. Idempotent server-side on an already-closed sitting. */
export async function closePlaySession(
  playSessionId: string,
  body: ClosePlaySessionRequest,
): Promise<void> {
  await axios.post(`${baseUrl}exercise-play-session/${playSessionId}/close`, body);
}
